import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import {
  getAdminPosting, getPostingApplicants, updateApplicant, extractApiErrorMessage,
} from '../../../api/adminApi';
import FadeImage from '../../../shared/components/FadeImage';
import {
  APPLICANT_STATUSES, readApplicantStatus, statusMeta, statusPatch, visibleStatuses,
  isTerminal, selectableStatuses,
} from '../../../shared/applications/applicantStatus';
import { postingStatusMeta } from '../../../shared/castings/postingStatus';

const AVATAR_COLORS = ['#7F77DD','#D4537E','#1D9E75','#BA7517','#185FA5','#993556'];
const getColor = id => AVATAR_COLORS[Number(id) % AVATAR_COLORS.length];

/**
 * 지원자 아바타.
 *
 * 28~52px로 그리면서 원본을 통째로 내려받으면 지원자가 수십 명일 때 눈에 띄게 느려진다.
 * CDN 썸네일(300px)을 먼저 받고, 아직 안 만들어졌으면 원본으로 물러난다.
 * 사진이 아예 없으면 이름 두 글자를 쓴다 — 실루엣보다 알아보기 쉽다.
 */
function ActorAvatar({ src, name, size, color, className = 'ag-avatar', fontSize }) {
  if (!src) {
    return (
      <div className={className} style={{ background: color + '22', color, fontSize, fontWeight: 700, flexShrink: 0 }}>
        {(name ?? '?').slice(0, 2)}
      </div>
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
      <FadeImage src={src} alt={name} size="thumb" placeholder="talent" />
    </div>
  );
}

/**
 * 전형 상태 고르기.
 *
 * 끝난 지원(합격·탈락·취소)은 서버가 변경을 거부하므로 잠근다 —
 * 고를 수는 있는데 저장만 실패하면 왜 안 되는지 알 수 없다.
 */
function StatusSelect({ value, onChange, style, disabled }) {
  const terminal = isTerminal(value);
  const meta = statusMeta(value);
  return (
    <select
      className="ag-field__input"
      style={style}
      value={value}
      disabled={disabled || terminal}
      title={terminal ? `${meta.label} — 끝난 지원이라 상태를 바꿀 수 없습니다` : undefined}
      onChange={e => onChange(e.target.value)}
    >
      {selectableStatuses(value).map(st => (
        <option key={st.key} value={st.key} disabled={st.disabled}>{st.label}</option>
      ))}
    </select>
  );
}

// ── 지원자 카드 ───────────────────────────────────────────────────────────────
function ApplicantCard({ applicant, onStatusChange, onCardClick }) {
  const color = getColor(applicant.actorId ?? applicant.id);
  const meta = statusMeta(applicant.status);

  return (
    <div
      style={{
        background: '#fff',
        border: '0.5px solid var(--color-border-tertiary)',
        borderRadius: 8,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
      }}
      onClick={() => onCardClick(applicant)}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* 배우 정보 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ActorAvatar
          src={applicant.actorProfileImage}
          name={applicant.actorName}
          size={36}
          color={color}
          fontSize={11}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {applicant.actorName}
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
            {[applicant.gender === 'MALE' ? '남' : applicant.gender === 'FEMALE' ? '여' : null, applicant.birthDate?.slice(0,4)].filter(Boolean).join(' · ')}
            {applicant.height ? ` · ${applicant.height}cm` : ''}
          </div>
        </div>
        <span className={`ag-badge ${meta.cls}`} style={{ flexShrink: 0 }}>{meta.short}</span>
      </div>

      {/* 다음 단계로 보내기 */}
      <div onClick={e => e.stopPropagation()}>
        <StatusSelect
          value={applicant.status}
          onChange={next => onStatusChange(applicant.id, next)}
          style={{ fontSize: 11, padding: '3px 6px', width: '100%' }}
        />
      </div>

      {applicant.memo && (
        <div style={{
          fontSize: 11, color: 'var(--color-text-secondary)',
          background: 'var(--color-background-secondary)',
          borderRadius: 4, padding: '4px 8px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {applicant.memo}
        </div>
      )}

      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
        지원일 {applicant.appliedAt?.slice(0, 10) ?? '—'}
      </div>
    </div>
  );
}

// ── 지원자 상세 모달 ──────────────────────────────────────────────────────────
function ApplicantModal({ applicant, postingId, onClose, onUpdated }) {
  const [memo,    setMemo]    = useState(applicant.memo ?? '');
  const [status,  setStatus]  = useState(applicant.status);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await updateApplicant(postingId, applicant.id, { ...statusPatch(status, message), memo });
      onUpdated({ ...applicant, status, memo });
      onClose();
    } catch (err) {
      // 저장 실패를 alert으로 띄우고 닫으면, 왜 안 됐는지도 모르고 입력도 날아간다
      setSaveError(extractApiErrorMessage(err, `저장하지 못했습니다. (HTTP ${err.response?.status ?? '네트워크'})`));
    } finally {
      setSaving(false);
    }
  };

  const color = getColor(applicant.actorId ?? applicant.id);

  return (
    <div className="ag-modal-backdrop" onClick={onClose}>
      <div
        className="ag-modal"
        style={{ width: 440, maxWidth: '90vw' }}
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <ActorAvatar
            src={applicant.actorProfileImage}
            name={applicant.actorName}
            size={52}
            color={color}
            className="ag-avatar ag-avatar--lg"
          />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{applicant.actorName}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 3 }}>
              {[
                applicant.gender === 'MALE' ? '남성' : applicant.gender === 'FEMALE' ? '여성' : null,
                applicant.birthDate?.slice(0, 4) ? `${applicant.birthDate.slice(0, 4)}년생` : null,
                applicant.height ? `${applicant.height}cm` : null,
                applicant.weight ? `${applicant.weight}kg` : null,
              ].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>

        {/* 전형 상태 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>전형 상태</div>
          <StatusSelect value={status} onChange={setStatus} style={{ width: '100%' }} />
        </div>

        {/* 배우에게 함께 갈 말 — 상태가 바뀔 때만 보낸다 */}
        {status !== applicant.status && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
              배우에게 보낼 메시지 (선택)
            </div>
            <input
              className="ag-field__input"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="예: 1차 오디션 통과를 축하드립니다"
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
              지원 이력에 남고 배우에게 알림으로 갑니다.
            </div>
          </div>
        )}

        {/* 메모 */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
            메모 <span style={{ color: 'var(--color-text-tertiary)' }}>— 어드민만 봅니다</span>
          </div>
          <textarea
            className="ag-field__input"
            rows={3}
            placeholder="메모를 입력하세요..."
            value={memo}
            onChange={e => setMemo(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        </div>

        {/* 지원일 */}
        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 20 }}>
          지원일: {applicant.appliedAt?.slice(0, 10) ?? '—'}
        </div>

        {saveError && (
          <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 12 }}>
            <i className="ti ti-alert-triangle" /> {saveError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ag-btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>닫기</button>
          <button
            className="ag-btn ag-btn--primary"
            style={{ flex: 1, justifyContent: 'center' }}
            onClick={handleSave}
            disabled={saving}
          >
            <i className="ti ti-check" /> {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
export default function AdminPostingDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [posting,    setPosting]    = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [selected,   setSelected]   = useState(null); // 모달 열린 지원자
  // 지원자 조회만 실패한 경우. 공고는 보여주되 '0명'을 사실로 오해하지 않도록 알린다.
  const [applicantError, setApplicantError] = useState(null);
  const [viewMode,   setViewMode]   = useState('kanban'); // 'kanban' | 'table'

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setApplicantError(null);
      try {
        // 지원자 조회가 실패해도 공고 자체는 보여준다. 다만 실패 사실은 숨기지 않는다 —
        // 조용히 빈 배열로 두면 '지원자 0명'과 구분되지 않는다.
        const [post, apps] = await Promise.all([
          getAdminPosting(id),
          getPostingApplicants(id).catch(err => ({ __failed: err })),
        ]);
        if (cancelled) return;
        setPosting(post);

        if (apps && apps.__failed) {
          const code = apps.__failed.response?.status;
          setApplicantError(
            `지원자 목록을 불러오지 못했습니다. 아래 인원수는 실제와 다를 수 있습니다.${code ? ` (HTTP ${code})` : ''}`,
          );
          setApplicants([]);
          return;
        }

        // 서버는 지원 하나에 status 하나만 준다. 화면도 그 값을 그대로 쓴다.
        setApplicants((apps ?? []).map(a => ({ ...a, status: readApplicantStatus(a) })));
      } catch (err) {
        if (!cancelled) {
          const code = err.response?.status;
          setError(`공고 정보를 불러오지 못했습니다.${code ? ` (HTTP ${code})` : ''}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  /**
   * 낙관적으로 먼저 바꾸고, 서버가 거부하면 되돌린다.
   *
   * 이전 값은 낙관적 갱신 '전에' 붙잡아 둬야 한다. 갱신 뒤에 읽으면 이미 새 값이라
   * 되돌려도 그대로다 — 서버가 거부해도 화면은 성공한 것처럼 보인다.
   */
  const changeStatus = useCallback(async (applicantId, newStatus) => {
    let previous;
    setApplicants(prev => prev.map(a => {
      if (a.id !== applicantId) return a;
      previous = a.status;
      return { ...a, status: newStatus };
    }));

    try {
      await updateApplicant(id, applicantId, statusPatch(newStatus));
      setApplicantError(null);
    } catch (err) {
      setApplicants(prev =>
        prev.map(a => (a.id === applicantId ? { ...a, status: previous } : a))
      );
      // 왜 거부됐는지 서버가 말해주면 그대로 보여준다 — '저장 실패'만 보면 손쓸 수가 없다
      const detail = extractApiErrorMessage(err);
      setApplicantError(
        `단계를 바꾸지 못해 되돌렸습니다.${detail ? ` ${detail}` : ''} (HTTP ${err.response?.status ?? '네트워크'})`,
      );
    }
  }, [id]);

  const handleUpdated = useCallback((updated) => {
    setApplicants(prev => prev.map(a => a.id === updated.id ? updated : a));
  }, []);

  if (loading) {
    return (
      <AdminLayout pageId="postings">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
          <i className="ti ti-loader" style={{ fontSize: 24 }} /> 불러오는 중...
        </div>
      </AdminLayout>
    );
  }

  if (error || !posting) {
    return (
      <AdminLayout pageId="postings">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-danger)' }}>
          {error ?? '공고를 찾을 수 없습니다.'}
        </div>
      </AdminLayout>
    );
  }

  const postStatus = postingStatusMeta(posting.status);

  // 상태별 지원자 그룹
  const columns = visibleStatuses(applicants.map(a => a.status));
  const byStatus = Object.fromEntries(
    APPLICANT_STATUSES.map(st => [st.key, applicants.filter(a => a.status === st.key)])
  );

  const hired = byStatus.HIRED.length;

  return (
    <AdminLayout pageId="postings">
      {selected && (
        <ApplicantModal
          applicant={selected}
          postingId={id}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => { handleUpdated(updated); setSelected(null); }}
        />
      )}

      <div style={{ padding: '0 0 40px' }}>
        {/* 상단 헤더 */}
        <div className="admin-topbar" style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="ag-btn ag-btn--sm" onClick={() => navigate('/admin/postings')}>
              <i className="ti ti-arrow-left" /> 목록
            </button>
            <h1 style={{ fontSize: 17 }}>{posting.title}</h1>
            <span className={`ag-badge ${postStatus.cls}`}>{postStatus.label}</span>
          </div>
          <div className="admin-topbar__right">
            {/* 뷰 토글 */}
            <div style={{ display: 'flex', border: '0.5px solid var(--color-border-secondary)', borderRadius: 6, overflow: 'hidden' }}>
              {[{ v: 'kanban', icon: 'ti-layout-columns', label: '칸반' }, { v: 'table', icon: 'ti-list', label: '목록' }].map(({ v, icon, label }) => (
                <button
                  key={v}
                  onClick={() => setViewMode(v)}
                  style={{
                    padding: '5px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
                    background: viewMode === v ? 'var(--color-primary)' : 'transparent',
                    color: viewMode === v ? '#fff' : 'var(--color-text-secondary)',
                    border: 'none', cursor: 'pointer',
                  }}
                >
                  <i className={`ti ${icon}`} /> {label}
                </button>
              ))}
            </div>
            <span className="admin-badge"><i className="ti ti-user" /> admin</span>
          </div>
        </div>

        {/* 공고 기본 정보 + 통계 요약 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, margin: '20px 0' }}>
          {[
            { icon: 'ti-building', label: '제작사',   value: posting.companyName ?? '—' },
            { icon: 'ti-users',    label: '총 지원자', value: `${applicants.length}명` },
            { icon: 'ti-check',    label: '최종 합격',  value: `${hired}명`, color: 'var(--color-success)' },
            { icon: 'ti-calendar', label: '마감일',    value: posting.deadline?.slice(0, 10) ?? '—' },
          ].map(({ icon, label, value, color }) => (
            <div key={label} className="ag-card" style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <i className={`ti ${icon}`} /> {label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: color ?? 'var(--color-text-primary)' }}>
                {value}
              </div>
            </div>
          ))}
        </div>

        {applicantError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
            padding: '10px 14px', borderRadius: 8,
            border: '1px solid var(--color-danger)', color: 'var(--color-danger)', fontSize: 12,
          }}>
            <i className="ti ti-alert-triangle" />
            <span>{applicantError}</span>
          </div>
        )}

        {/* 상태별 요약 바 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {columns.map(st => (
            <div
              key={st.key}
              style={{
                flex: '1 1 120px', padding: '10px 14px',
                background: 'var(--color-background-secondary)',
                borderRadius: 8,
                borderLeft: `3px solid ${st.color}`,
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{st.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: st.color, marginTop: 2 }}>
                {byStatus[st.key].length}명
              </div>
            </div>
          ))}
        </div>

        {applicants.length === 0 ? (
          <div className="ag-card" style={{ padding: '48px 0', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
            <i className="ti ti-users" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} />
            아직 지원자가 없습니다.
          </div>
        ) : viewMode === 'kanban' ? (
          /* ── 칸반 뷰 ── */
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
            gap: 14, alignItems: 'start',
          }}>
            {columns.map(st => (
              <div key={st.key}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 10, padding: '0 2px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: st.color }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: st.color }}>{st.short}</span>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: '#fff',
                    background: st.color, borderRadius: 20, padding: '1px 7px',
                  }}>
                    {byStatus[st.key].length}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {byStatus[st.key].length === 0 ? (
                    <div style={{
                      border: '1.5px dashed var(--color-border-tertiary)',
                      borderRadius: 8, padding: '20px 0',
                      textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 12,
                    }}>
                      없음
                    </div>
                  ) : byStatus[st.key].map(applicant => (
                    <ApplicantCard
                      key={applicant.id}
                      applicant={applicant}
                      onStatusChange={changeStatus}
                      onCardClick={setSelected}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ── 테이블 뷰 ── */
          <div className="ag-card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="ag-table">
              <thead>
                <tr>
                  {['이름', '성별/나이', '키/몸무게', '전형 상태', '지원일', '메모', ''].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {applicants.map(a => {
                  const color = getColor(a.actorId ?? a.id);
                  return (
                    <tr key={a.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ActorAvatar
                            src={a.actorProfileImage}
                            name={a.actorName}
                            size={28}
                            color={color}
                            fontSize={10}
                          />
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{a.actorName}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {a.gender === 'MALE' ? '남' : a.gender === 'FEMALE' ? '여' : '—'}
                        {a.birthDate ? ` · ${a.birthDate.slice(0, 4)}` : ''}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {a.height ? `${a.height}cm` : '—'}{a.weight ? ` / ${a.weight}kg` : ''}
                      </td>
                      <td>
                        <StatusSelect
                          value={a.status}
                          onChange={next => changeStatus(a.id, next)}
                          style={{ fontSize: 11, padding: '3px 6px' }}
                        />
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                        {a.appliedAt?.slice(0, 10) ?? '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-secondary)', maxWidth: 160 }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.memo ?? '—'}
                        </div>
                      </td>
                      <td>
                        <button className="ag-btn ag-btn--sm" onClick={() => setSelected(a)}>
                          <i className="ti ti-edit" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
